import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, HostListener, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';
import { ContainerComponent } from '../../layout/container/container.component';
import { CarouselService, CarouselItem } from '../../../services/carousel.service';
import { AnalyticsService } from '../../../services/analytics.service';
import { Subscription, interval, takeUntil, Subject } from 'rxjs';

// Declaraciones de tipos para Google Analytics y GTM
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
  const gtag: (...args: any[]) => void;
}

@Component({
  selector: 'app-home-hero',
  standalone: true,
  imports: [CommonModule, RouterModule, HttpClientModule, ContainerComponent],
  templateUrl: './home-hero.component.html',
  styleUrl: './home-hero.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeHeroComponent implements OnInit, OnDestroy, AfterViewInit {
  carouselItems: CarouselItem[] = [];
  currentSlideIndex = 0;
  isLoading = true;
  error = false;

  mostrarModal = true; // o false, según cuándo quieras mostrarlo

 

  private subscription = new Subscription();
  private destroy$ = new Subject<void>();
  private autoPlayInterval = 16000; // 5 segundos
  public deviceType: 'xl' | 'desktop' | 'tablet' | 'movil';
  private resizeTimeout: any;
  private autoPlaySubscription: Subscription | null = null;
  private isAutoPlayActive = false; // Nueva propiedad para controlar el estado
  private trackedSlides = new Set<number>(); // Para trackear cada slide solo una vez

  @ViewChild('sectionElement', { static: false }) sectionElement?: ElementRef;

  constructor(
    private carouselService: CarouselService,
    private analyticsService: AnalyticsService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    // Detectar tipo de dispositivo inicial
    this.deviceType = this.carouselService.getCurrentDeviceType();
  }

  ngOnInit() {
    this.loadCarouselItems();
  }

  ngAfterViewInit() {
    // Registrar la sección para tracking de analytics
    if (this.sectionElement?.nativeElement) {
      this.analyticsService.registerSectionForTracking(
        this.sectionElement.nativeElement,
        'Home Hero',
        'home-hero-section'
      );
    }
  }

   cerrarModal() {
    this.mostrarModal = false;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    // Limpiar también el autoplay subscription
    this.stopAutoPlay();

    // Unregister section from analytics tracking
    if (this.sectionElement?.nativeElement) {
      this.analyticsService.unregisterSectionFromTracking(this.sectionElement.nativeElement);
    }
  }

  // Listener para cambios de tamaño de ventana
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    // Debounce para evitar múltiples llamadas durante el resize
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      // Usar el método del servicio para actualizar el tipo de dispositivo
      this.carouselService.updateDeviceType();

      const newDeviceType = this.carouselService.getCurrentDeviceType();

      // Solo recargar si cambió el tipo de dispositivo
      if (newDeviceType !== this.deviceType) {
        console.log(`[HomeHero] Dispositivo cambiado de ${this.deviceType} a ${newDeviceType}`);
        this.deviceType = newDeviceType;
        this.currentSlideIndex = 0; // Resetear al primer slide
        this.loadCarouselItems();
      }
    }, 300); // Esperar 300ms después del último resize
  }

  private loadCarouselItems() {
    this.isLoading = true;
    this.error = false;
    this.cdr.markForCheck();

    // Limpiar tracking de slides cuando se recargan los datos
    this.clearSlideTracking();

    // Detener autoplay antes de cargar nuevos items
    this.stopAutoPlay();

    this.subscription.add(
      this.carouselService.getCarouselByDeviceType(this.deviceType)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (items) => {
            this.carouselItems = items.sort((a, b) => a.orden - b.orden);
            this.isLoading = false;
            this.startAutoPlay(); // Solo iniciar si hay más de 1 item
            this.cdr.markForCheck();

            // Track initial promotion view
            if (this.carouselItems.length > 0) {
              setTimeout(() => this.trackPromotionView(), 100);
            }
          },
          error: (error) => {
            console.error('Error loading carousel items:', error);
            this.error = true;
            this.isLoading = false;
            this.cdr.markForCheck();
          }
        })
    );
  }

  private startAutoPlay() {

    // Solo iniciar si hay más de 1 slide y no está ya activo
    if (this.carouselItems.length > 1 && !this.isAutoPlayActive) {
      this.stopAutoPlay(); // Asegurar que no hay suscripciones previas

      this.isAutoPlayActive = true;
      this.autoPlaySubscription = interval(this.autoPlayInterval)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.nextSlide();
        });

      console.log(`[HomeHero] Autoplay iniciado - ${this.autoPlayInterval}ms`);
    }
  }

  private stopAutoPlay() {
    if (this.autoPlaySubscription) {
      this.autoPlaySubscription.unsubscribe();
      this.autoPlaySubscription = null;
    }
    this.isAutoPlayActive = false;
    console.log('[HomeHero] Autoplay detenido');
  }

  // Método para pausar el autoplay (para hover)
  pauseAutoPlay() {
    if (this.isAutoPlayActive && this.autoPlaySubscription) {
      this.autoPlaySubscription.unsubscribe();
      this.autoPlaySubscription = null;
      console.log('[HomeHero] Autoplay pausado por hover');
    }
  }

  // Método para reanudar el autoplay (para hover)
  resumeAutoPlay() {
    if (this.isAutoPlayActive && this.carouselItems.length > 1 && !this.autoPlaySubscription) {
      this.autoPlaySubscription = interval(this.autoPlayInterval)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.nextSlide();
        });
      console.log('[HomeHero] Autoplay reanudado después de hover');
    }
  }

  // Método para manejar el hover (mouseenter)
  onCarouselMouseEnter() {
    this.pauseAutoPlay();
  }

  // Método para manejar cuando sale el mouse (mouseleave)
  onCarouselMouseLeave() {
    this.resumeAutoPlay();
  }

  nextSlide() {
    if (this.carouselItems.length > 1) {
      this.currentSlideIndex = (this.currentSlideIndex + 1) % this.carouselItems.length;
      this.cdr.markForCheck();
      // Track promotion view when slide changes
      setTimeout(() => this.trackPromotionView(), 100);
    }
  }

  prevSlide() {
    if (this.carouselItems.length > 1) {
      this.currentSlideIndex = this.currentSlideIndex === 0
        ? this.carouselItems.length - 1
        : this.currentSlideIndex - 1;
      this.cdr.markForCheck();
      // Track promotion view when slide changes
      setTimeout(() => this.trackPromotionView(), 100);
    }
  }

  goToSlide(index: number) {
    this.currentSlideIndex = index;
    this.cdr.markForCheck();
    // Track promotion view when slide changes
    setTimeout(() => this.trackPromotionView(), 100);
  }

  get currentSlide(): CarouselItem | null {
    return this.carouselItems[this.currentSlideIndex] || null;
  }

  onImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/images/web002-invitalos-perro.png';
  }

  autoApertura(){
    window.open('https://www.unipay.cl/autoatencion-ptoventa/validar-identidad?utm_source=Digital&utm_medium=Pagina_Unipay&utm_campaign=Bannerhome', '_blank', 'noopener,noreferrer');
  }

  getTargetAttribute(): string {
    return (this.currentSlide?.url && this.currentSlide.url !== '#') ? '_blank' : '_self';
  }

  isImageClickable(): boolean {
    return !!(this.currentSlide?.url && this.currentSlide.url !== '#');
  }

  getCurrentWidth(): number {
    return window.innerWidth;
  }

  onImageClick(): void {
    if (this.currentSlide?.url && this.currentSlide.url !== '#') {
      // Track promotion selection event
      this.trackPromotionSelection();

      // Si es una URL externa, abrir en nueva pestaña
      if (this.currentSlide.url.startsWith('http')) {
        window.open(this.currentSlide.url, '_blank', 'noopener,noreferrer');
      } else {
        // Si es una ruta interna, usar router
        this.router.navigate([this.currentSlide.url]);
      }
    }
  }

  // Método para rastrear la selección de promoción
  private trackPromotionSelection(): void {
    if (!this.currentSlide) return;

    const eventData = this.analyticsService.createPromotionEventData(
      this.currentSlide.promotionId || 0,
      this.deviceType,
      this.currentSlide.promotionName || '',
      this.currentSlide.createName || '',
      `carousel_slide_${this.currentSlideIndex + 1}`
    );

    this.analyticsService.trackPromotionSelection(eventData);
  }

  // Método para rastrear cuando se muestra una promoción (view) - solo una vez por slide
  private trackPromotionView(): void {
    if (!this.currentSlide || !this.sectionElement?.nativeElement) return;

    // Verificar si ya se trackeó este slide
    if (this.trackedSlides.has(this.currentSlideIndex)) {
      return;
    }

    const eventData = this.analyticsService.createPromotionEventData(
      this.currentSlide.id || 0,
      this.deviceType,
      this.currentSlide.titulo || '',
      this.currentSlide.img || '',
      `carousel_slide_${this.currentSlideIndex + 1}`
    );

    // Marcar este slide como trackeado
    this.trackedSlides.add(this.currentSlideIndex);

    // Usar tracking directo para el carrusel ya que siempre está visible
    this.analyticsService.trackPromotionView(eventData);
  }

  // Método para limpiar el tracking de slides (útil si se recargan los datos)
  private clearSlideTracking(): void {
    this.trackedSlides.clear();
  }

  // Método para debugging - ver estado del tracking
  debugSlideTracking(): void {
    console.log('🔍 Carrusel: Estado del tracking de slides');
    console.log('📊 Slides trackeados:', Array.from(this.trackedSlides));
    console.log('📊 Slide actual:', this.currentSlideIndex);
    console.log('📊 Total de slides:', this.carouselItems.length);
  }
}


